import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, ExternalLink, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { SEO, SchemaOrg } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AFFILIATE = [
  { name: 'Fashionphile', url: 'https://www.fashionphile.com/jewelry', testid: 'affiliate-btn-fashionphile' },
  { name: 'The RealReal', url: 'https://www.therealreal.com/fine-jewelry', testid: 'affiliate-btn-therealreal' },
  { name: '1stDibs', url: 'https://www.1stdibs.com/jewelry/', testid: 'affiliate-btn-1stdibs' },
  { name: 'Vestiaire Collective', url: 'https://www.vestiairecollective.com/jewelry/', testid: 'affiliate-btn-vestiaire' },
];

export const DesignerPage = () => {
  const { slug } = useParams();
  const [designer, setDesigner] = useState(null);
  const [allDesigners, setAllDesigners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [designerRes, allDesignersRes] = await Promise.all([
          axios.get(`${API}/designers/${slug}`),
          axios.get(`${API}/designers`),
        ]);
        setDesigner(designerRes.data);
        setAllDesigners(allDesignersRes.data);
        setError(null);
      } catch (err) {
        setError('Designer not found');
        console.error('Error fetching designer:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]">
        <div className="w-6 h-6 border border-[#111] border-t-transparent animate-spin" />
      </div>
    );
  }

  if (error || !designer) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAF9F6]">
        <p className="overline overline-gold mb-4">404</p>
        <h1 className="heading-editorial text-4xl mb-8">Designer Not Found</h1>
        <Link to="/" className="btn-editorial">Return Home</Link>
      </div>
    );
  }

  const currentIndex = allDesigners.findIndex((d) => d.slug === slug);
  const prevDesigner = currentIndex > 0 ? allDesigners[currentIndex - 1] : null;
  const nextDesigner = currentIndex < allDesigners.length - 1 ? allDesigners[currentIndex + 1] : null;

  return (
    <div data-testid={`designer-page-${slug}`} className="bg-[#FAF9F6]">
      <SEO
        title={`${designer.name} Pre-Owned Buying Guide`}
        description={`Authentication tips, signature pieces, and resale value for pre-owned ${designer.name} jewelry. ${designer.tagline}`}
        image={designer.image_url}
        url={`/designers/${designer.slug}`}
        type="website"
        keywords={`${designer.name}, pre-owned ${designer.name}, ${designer.name} authentication, ${designer.name} resale`}
      />
      <SchemaOrg
        type="brand"
        data={{
          name: `Pre-Owned ${designer.name}`,
          description: designer.description,
          image: designer.image_url,
          url: `/designers/${designer.slug}`,
        }}
      />
      <SchemaOrg
        type="breadcrumb"
        data={{
          items: [
            { name: 'Home', url: '/' },
            { name: 'Designers', url: '/' },
            { name: designer.name, url: `/designers/${designer.slug}` },
          ],
        }}
      />

      {/* Editorial Hero */}
      <section className="relative bg-[#111] text-white">
        <div className="absolute inset-0 z-0 opacity-50">
          <img src={designer.image_url} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-[#111]/70 to-[#111]/30" />
        </div>

        <div className="relative z-10 container-editorial pt-24 pb-20 lg:pt-32 lg:pb-28">
          <nav className="flex items-center gap-3 text-xs tracking-[0.15em] uppercase text-white/60 mb-12">
            <Link to="/" className="hover:text-[#C69C6D] transition-colors">Home</Link>
            <span>/</span>
            <span>Designers</span>
            <span>/</span>
            <span className="text-white">{designer.name}</span>
          </nav>

          <p className="overline text-[#C69C6D] mb-6">
            {designer.origin} · Established {designer.founded}
          </p>
          <h1 className="font-heading font-light text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight text-white mb-8">
            {designer.name}
          </h1>
          <p className="font-heading italic text-2xl lg:text-3xl text-white/85 max-w-3xl">
            {designer.tagline}
          </p>
        </div>
      </section>

      {/* Affiliate Strip */}
      <section className="border-b border-[#E5E0D8] bg-[#FAF9F6]">
        <div className="container-editorial py-8">
          <div className="flex items-center justify-between flex-wrap gap-6">
            <p className="overline overline-gold">Where to Shop Pre-Owned {designer.name}</p>
            <div className="flex flex-wrap gap-3">
              {AFFILIATE.slice(0, 3).map((p) => (
                <a
                  key={p.name}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-editorial inline-flex items-center gap-2 text-[11px]"
                  data-testid={p.testid}
                >
                  Shop on {p.name}
                  <ExternalLink className="w-3 h-3" strokeWidth={1.5} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-editorial">
        <div className="container-editorial">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Left Column — Editorial Body */}
            <div className="lg:col-span-8">
              <p className="overline overline-gold mb-6">The House</p>
              <h2 className="heading-editorial text-4xl lg:text-5xl mb-8">
                About <span className="italic">{designer.name}</span>
              </h2>
              <p className="text-xl text-[#555] leading-relaxed font-light mb-12">
                {designer.description}
              </p>

              {/* Signature Pieces */}
              <div className="mb-16">
                <div className="flex items-baseline justify-between mb-8 pb-4 border-b border-[#E5E0D8]">
                  <p className="overline overline-gold">Signature Collections</p>
                  <span className="overline">{designer.signature_pieces.length} Iconic Pieces</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E5E0D8]">
                  {designer.signature_pieces.map((piece, i) => (
                    <div key={i} className="bg-[#FAF9F6] p-6 group">
                      <p className="font-heading text-2xl text-[#111] mb-2 group-hover:text-[#C69C6D] transition-colors">
                        {piece}
                      </p>
                      <p className="overline">Iconic · {designer.name}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Authentication */}
              <div className="mb-16" data-testid="authentication-section">
                <div className="flex items-baseline justify-between mb-8 pb-4 border-b border-[#E5E0D8]">
                  <p className="overline overline-gold">Authentication</p>
                  <Link
                    to="/guides/authenticate-luxury-jewelry"
                    className="text-xs tracking-[0.15em] uppercase hover:text-[#C69C6D] transition-colors inline-flex items-center gap-2"
                  >
                    Full Guide <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                  </Link>
                </div>
                <h3 className="heading-editorial text-3xl lg:text-4xl mb-8">
                  How to Verify <span className="italic">Authentic</span> {designer.name}
                </h3>
                <ul className="space-y-6">
                  {designer.authentication_tips.map((tip, i) => (
                    <li key={i} className="flex items-start gap-5 pb-6 border-b border-[#E5E0D8] last:border-b-0">
                      <span className="flex-shrink-0 w-8 h-8 border border-[#C69C6D] flex items-center justify-center text-[#C69C6D] text-xs font-medium mt-0.5">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <p className="text-[#555] leading-relaxed flex-1">{tip}</p>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Resale Value */}
              <div data-testid="resale-section">
                <div className="flex items-baseline justify-between mb-8 pb-4 border-b border-[#E5E0D8]">
                  <p className="overline overline-gold">Investment Outlook</p>
                  <Link
                    to="/guides/resale-value-guide"
                    className="text-xs tracking-[0.15em] uppercase hover:text-[#C69C6D] transition-colors inline-flex items-center gap-2"
                  >
                    Full Guide <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                  </Link>
                </div>
                <h3 className="heading-editorial text-3xl lg:text-4xl mb-6">
                  Resale <span className="italic">Value</span>
                </h3>
                <blockquote className="border-l-2 border-[#C69C6D] pl-8 py-2">
                  <p className="font-heading italic text-xl lg:text-2xl text-[#111] leading-relaxed">
                    {designer.resale_value}
                  </p>
                </blockquote>
              </div>
            </div>

            {/* Right Column — Sticky Sidebar */}
            <aside className="lg:col-span-4">
              <div className="sticky top-32 space-y-12">
                {/* Quick Facts */}
                <div className="border border-[#E5E0D8] p-8">
                  <p className="overline overline-gold mb-6">Quick Facts</p>
                  <dl className="space-y-5">
                    <div>
                      <dt className="overline mb-1.5">Founded</dt>
                      <dd className="font-heading text-2xl text-[#111]">{designer.founded}</dd>
                    </div>
                    <div className="pt-5 border-t border-[#E5E0D8]">
                      <dt className="overline mb-1.5">Origin</dt>
                      <dd className="font-heading text-2xl text-[#111]">{designer.origin}</dd>
                    </div>
                    <div className="pt-5 border-t border-[#E5E0D8]">
                      <dt className="overline mb-1.5">Iconic Pieces</dt>
                      <dd className="font-heading text-2xl text-[#111]">
                        {designer.signature_pieces.length}+
                      </dd>
                    </div>
                  </dl>
                </div>

                {/* Where to Buy */}
                <div className="bg-[#111] text-white p-8" data-testid="where-to-buy-section">
                  <p className="overline text-[#C69C6D] mb-6">Where to Buy</p>
                  <h3 className="font-heading text-2xl font-light leading-tight mb-6">
                    Pre-Owned <span className="italic">{designer.name}</span> Marketplaces
                  </h3>
                  <ul className="space-y-3">
                    {AFFILIATE.map((p) => (
                      <li key={p.name}>
                        <a
                          href={p.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group flex items-center justify-between py-3 border-b border-white/10 hover:border-[#C69C6D] transition-colors"
                          data-testid={p.testid}
                        >
                          <span className="text-white group-hover:text-[#C69C6D] transition-colors">
                            {p.name}
                          </span>
                          <ExternalLink className="w-3 h-3 text-white/40 group-hover:text-[#C69C6D] transition-colors" strokeWidth={1.5} />
                        </a>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 text-[10px] tracking-wider uppercase text-white/40">
                    Affiliate links · We may earn a commission
                  </p>
                </div>

                {/* Trust Badges */}
                <div>
                  <p className="overline overline-gold mb-4">Why We Cover {designer.name}</p>
                  <ul className="space-y-3">
                    {[
                      'GIA-trained authenticators on staff',
                      'Hands-on inspection of pieces',
                      'Independent editorial coverage',
                    ].map((line) => (
                      <li key={line} className="flex items-start gap-3">
                        <CheckCircle className="w-4 h-4 text-[#C69C6D] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                        <span className="text-sm text-[#555]">{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Designer Navigation */}
      <section className="border-t border-[#E5E0D8] bg-[#111] text-white">
        <div className="container-editorial py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {prevDesigner ? (
              <Link to={`/designers/${prevDesigner.slug}`} className="group flex items-center gap-4 md:border-r border-white/10 md:pr-8">
                <ArrowLeft className="w-4 h-4 text-[#C69C6D] group-hover:-translate-x-1 transition-transform" strokeWidth={1.5} />
                <div>
                  <p className="overline text-[#C69C6D] mb-1.5">Previous House</p>
                  <p className="font-heading text-2xl text-white group-hover:text-[#C69C6D] transition-colors">
                    {prevDesigner.name}
                  </p>
                </div>
              </Link>
            ) : (
              <div />
            )}

            {nextDesigner ? (
              <Link to={`/designers/${nextDesigner.slug}`} className="group flex items-center gap-4 md:justify-end md:text-right md:pl-8">
                <div className="md:order-2">
                  <p className="overline text-[#C69C6D] mb-1.5">Next House</p>
                  <p className="font-heading text-2xl text-white group-hover:text-[#C69C6D] transition-colors">
                    {nextDesigner.name}
                  </p>
                </div>
                <ArrowRight className="w-4 h-4 text-[#C69C6D] group-hover:translate-x-1 transition-transform md:order-3" strokeWidth={1.5} />
              </Link>
            ) : (
              <div />
            )}
          </div>
        </div>
      </section>

      {/* Other Designers Grid */}
      <section className="section-editorial">
        <div className="container-editorial">
          <div className="flex items-end justify-between mb-12 pb-6 border-b border-[#E5E0D8]">
            <div>
              <p className="overline overline-gold mb-3">The Houses</p>
              <h2 className="heading-editorial text-4xl lg:text-5xl">Explore More Designers</h2>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-px bg-[#E5E0D8]">
            {allDesigners
              .filter((d) => d.slug !== slug)
              .slice(0, 5)
              .map((d) => (
                <Link
                  key={d.id}
                  to={`/designers/${d.slug}`}
                  className="bg-[#FAF9F6] p-6 lg:p-8 text-center group hover:bg-white transition-colors"
                >
                  <div className="aspect-square overflow-hidden img-zoom mb-4 opacity-90 group-hover:opacity-100 transition-opacity">
                    <img src={d.image_url} alt={d.name} className="w-full h-full object-cover" />
                  </div>
                  <h3 className="font-heading text-lg text-[#111] group-hover:text-[#C69C6D] transition-colors">
                    {d.name}
                  </h3>
                </Link>
              ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default DesignerPage;
